import React, { useState, useEffect, useRef } from 'react';
import './Terminal.css';

// Import last updated date (generated at build time)
let lastUpdatedData;
try {
  lastUpdatedData = require('../last-updated.json');
} catch (e) {
  // Fallback if file doesn't exist (e.g., in development)
  lastUpdatedData = { lastUpdated: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) };
}

const Terminal = () => {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef(null);
  const terminalRef = useRef(null);
  const clickSoundRef = useRef(null);

  // Pre-load click sound
  useEffect(() => {
    const audio = new Audio('/sounds/click.mp3');
    audio.volume = 0.20;
    audio.preload = 'auto';
    clickSoundRef.current = audio;
  }, []);

  useEffect(() => {
    // Welcome message with ASCII art
    const asciiArt = `                                  
                             .==:-:-+=--:                             
                        =++.*@@@@@@@@@@@@#:..:                        
                       #=#@@@@@@@@@@@@@%@@@@@#+                       
                     +-@@@@%@@@@@@@%@#@@@%%@@@@=+                     
                    :*@@@@@@@@@@@%@@@@@@%@@@@@@@=                     
                    #@@@@@@@@@@@@@@@@@@@@@@@#%@@%=                    
                    =@@@@@@@@@@@@@@@@@@%@@@@@@@@@*                    
                    %@@@@@%:..........---=**@@@@@#                    
                    #@@@@+.=*+=-=+=++##++*#-.#@@@*                    
                    *@@@@@**##%@@@@##@%+=+#%+@@@@-                    
                     #@@@#-........::.....::.#@@%                     
                     @@@:#@@@@@@@@---*@@@@@@@=+@@                     
                     .@@.*=*@@@@@@@--@@-@@+@@=.@.                     
                     .%@-=%@@%@%@%@#.@@=++@@=..@.                     
                     ..@*+--=::++=%%.-@%#+=:..+@.                     
                      -%@#+=---=:##+...*.:--+##.                      
                       .*%#*=----@@@==@@*::=%%*.                      
                        +#%@#==-=====**.:+#@@=                        
                         -+#@+@@%++**%@#%@%%+.                        
                         #@*#*=:*@@%%*%@@*#*:                         
                         .@@%#@#===#@@#*##%%                          
                        ..@#@@@%=:..:-:-+@@*.                         
                       .::@@.-@@@@%=:.+@@@*=@..                       
                   ....++-.@@...:#@@@@@@%#==@@..                      
               ....:=*=*##+.@@-.:::::-=+*+==@%=...                    
             ..:+**%**==+=+-..@%::=+*#+*****@.:*:....                 
          ..:#%#*#++##==+-...-+%@=-=--+++%%@@.-+:.......              
       ....+**#*#**++%---#@@@@@#@@@@%*=:-+...-=:=.@@-:.....           
     ...=+++*+*++##++*#@@*+:.:--...=@@@*-*===++...%..=-:::..          
     .+==+*+++*%#+**+++==+=+=+@=+*%#:+%@#+==:-=.*@@+-=+=-=-.          
     .-+*=*+++*+%#+%***+*+*+*+=#*+*#@==*%.....@@@=:.:---==-.          
     .*-%*=*=++*-**+**+***+**+*+#*+++===:.@@@@@@=.:-==---==.          
     .@-*++**++#++#****++*******+++++=+#@@#....+*=====-=-=-.          
     .......................................................          `;

    const titleArt = `╻┏┓ ┏━┓┏━┓╻ ╻╻┏┳┓   ┏┳┓┏━┓╻ ╻┏━┓┏┳┓┏┳┓┏━┓╺┳┓
┃┣┻┓┣┳┛┣━┫┣━┫┃┃┃┃   ┃┃┃┃ ┃┣━┫┣━┫┃┃┃┃┃┃┣━┫ ┃┃
╹┗━┛╹┗╸╹ ╹╹ ╹╹╹ ╹   ╹ ╹┗━┛╹ ╹╹ ╹╹ ╹╹ ╹╹ ╹╺┻┛`;

    const asciiLines = asciiArt.split('\n').filter(line => line.trim() !== '').map(line => line.trim());
    const titleLines = titleArt.split('\n').filter(line => line.trim() !== '');
    
    const lastUpdatedDate = lastUpdatedData.lastUpdated;
    
    const welcomeOutput = [
      ...asciiLines.map((line) => ({
        type: 'output',
        content: line,
        className: 'ascii-art'
      })),
      { type: 'output', content: '', className: '' },
      ...titleLines.map((line) => ({
        type: 'output',
        content: line,
        className: 'title-art'
      })),
      { type: 'output', content: '', className: '' },
      { type: 'output', content: `Last updated ${lastUpdatedDate}`, className: 'info' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: 'Type \'help\' to see available commands.', className: 'info' },
      { type: 'output', content: '', className: '' }
    ];

    setHistory(welcomeOutput);
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const commands = {
    help: () => [
      { type: 'output', content: '  about          - About me', className: 'command-item' },
      { type: 'output', content: '  projects       - View my projects', className: 'command-item' },
      { type: 'output', content: '  experience     - Work experience', className: 'command-item' },
      { type: 'output', content: '  leadership     - Leadership experience', className: 'command-item' },
      { type: 'output', content: '  education      - Education', className: 'command-item' },
      { type: 'output', content: '  skills         - Technical skills', className: 'command-item' },
      { type: 'output', content: '  honors         - Awards & honors', className: 'command-item' },
      { type: 'output', content: '  certifications - View certifications', className: 'command-item' },
      { type: 'output', content: '  resume         - View resume', className: 'command-item' },
      { type: 'output', content: '  ls             - List available sections', className: 'command-item' },
      { type: 'output', content: '  cd <section>   - Navigate to a section', className: 'command-item' },
      { type: 'output', content: '  clear          - Clear terminal', className: 'command-item' },
      { type: 'output', content: '  help           - Show this help message', className: 'command-item' },
      { type: 'output', content: '', className: '' }
    ],
    about: () => [
      { type: 'output', content: '╔════════════════════════════════════════════════════════╗', className: 'box' },
      { type: 'output', content: '║                     ABOUT ME                           ║', className: 'box' },
      { type: 'output', content: '╚════════════════════════════════════════════════════════╝', className: 'box' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: 'I\'m a senior at the University of Houston majoring in Computer Science,', className: 'text' },
      { type: 'output', content: 'currently working at NSM-IT within the Natural Sciences and Mathematics College.', className: 'text' },
      { type: 'output', content: 'My position is a mix of IT support and application development, where I work on', className: 'text' },
      { type: 'output', content: 'enhancing workflows by automating the repetitive tasks and discovering the areas', className: 'text' },
      { type: 'output', content: 'that can be made more efficient through the use of Microsoft Power Platform tools', className: 'text' },
      { type: 'output', content: 'such as PowerApps and Power Automate.', className: 'text' },
      { type: 'output', content: '\n', className: '' },
      { type: 'output', content: 'I\'m really into the idea of constructing feasible solutions that lead to everyday', className: 'text' },
      { type: 'output', content: 'processes becoming smoother and more efficient. I am committed to learning new', className: 'text' },
      { type: 'output', content: 'technologies and coming up with solutions to combine my technical skills with the', className: 'text' },
      { type: 'output', content: 'non-technical world. Even better when done with a driven team!', className: 'text' },
      { type: 'output', content: '\n', className: '' },
      { type: 'output', content: 'If I am not working, I would probably be working on DIY projects in my backyard.', className: 'text' },
      { type: 'output', content: 'It\'s always rewarding to finish a project in any context, whether it\'s an MVP for', className: 'text' },
      { type: 'output', content: 'a hackathon or installing garden beds to plant fruits and vegetables. They both', className: 'text' },
      { type: 'output', content: 'pay off!', className: 'text' },
      { type: 'output', content: '\n', className: '' }
    ],
    projects: () => [
      { type: 'output', content: '╔════════════════════════════════════════════════════════╗', className: 'box' },
      { type: 'output', content: '║                    PROJECTS                            ║', className: 'box' },
      { type: 'output', content: '╚════════════════════════════════════════════════════════╝', className: 'box' },
      { type: 'output', content: '┌─ Annual Progress Evaluation Automation App | UH Dept. of Physics', className: 'project-title' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   Feb 2026', className: 'project-text' },
      { type: 'output', content: '│ Technologies:', className: 'project-label' },
      { type: 'output', content: '│   Power Platform, Power Apps, Power Automate, SharePoint, HTML, CSS', className: 'project-text' },
      { type: 'output', content: '│ Impact:', className: 'project-label' },
      { type: 'output', content: '│ - Replaced a manual process of collecting, compiling, and routing committee', className: 'project-text' },
      { type: 'output', content: '│   evaluation PDFs for ~30 student APEs per semester.', className: 'project-text' },
      { type: 'output', content: '│ - Built a Power Apps solution with sequential Power Automate routing,', className: 'project-text' },
      { type: 'output', content: '│   role-based access, digital signature capture, and a dynamic HTML', className: 'project-text' },
      { type: 'output', content: '│   scoresheet consolidating all committee scores and comments.', className: 'project-text' },
      { type: 'output', content: '│ - Reduced evaluation turnaround from 1–2 weeks to same-day completion,', className: 'project-text' },
      { type: 'output', content: '│   eliminating all manual compilation and PDF handling by staff.', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },

      { type: 'output', content: '┌─ ReVibe AI | CodeRED Astra Hackathon', className: 'project-title' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   Oct 2025', className: 'project-text' },
      { type: 'output', content: '│ Technologies:', className: 'project-label' },
      { type: 'output', content: '│   React, TailwindCSS, Express, Node.js, SQLite, Cloudinary, Gemini, SerpAPI', className: 'project-text' },
      { type: 'output', content: '│ Impact:', className: 'project-label' },
      { type: 'output', content: '│ - Addressed the overwhelming challenge of room design by simplifying', className: 'project-text' },
      { type: 'output', content: '│   brainstorming and furniture selection for users.', className: 'project-text' },
      { type: 'output', content: '│ - Integrated Google Gemini 2.5 Flash-Image & Pro models for image-to-image', className: 'project-text' },
      { type: 'output', content: '│   generation and JSON-based furniture metadata extraction; implemented', className: 'project-text' },
      { type: 'output', content: '│   SerpAPI\'s Google Shopping Scraper to convert Gemini outputs into product links.', className: 'project-text' },
      { type: 'output', content: '│ - Delivered a full-stack prototype that generated realistic redesigns with', className: 'project-text' },
      { type: 'output', content: '│   live product sourcing.', className: 'project-text' },
      { type: 'output', content: '│ Awards:', className: 'project-label' },
      { type: 'output', content: '│ - 2nd Place - CodeRED Astra Hackathon', className: 'award' },
      { type: 'output', content: '│ - Best Use of Gemini API - Major League Hacking', className: 'award' },
      { type: 'output', content: '│ Link to project:', className: 'project-label' },
      { type: 'output', content: '│   https://devpost.com/software/revibe-ai', className: 'link' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },

      { type: 'output', content: '┌─ Course Audit Workflow Automation App | NSM IT', className: 'project-title' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   July 2025', className: 'project-text' },
      { type: 'output', content: '│ Technologies:', className: 'project-label' },
      { type: 'output', content: '│   Power Platform, SharePoint, HTML, CSS', className: 'project-text' },
      { type: 'output', content: '│ Impact:', className: 'project-label' },
      { type: 'output', content: '│ - Replaced a manual, paper-based course audit process (~10 min per request).', className: 'project-text' },
      { type: 'output', content: '│ - Built a Power Apps + Power Automate solution with role-based inputs,', className: 'project-text' },
      { type: 'output', content: '│   digital signatures, PDF generation, and automated routing logic to digitize the workflow.', className: 'project-text' },
      { type: 'output', content: '│ - Reduced handling time by 90%+, saving about $4,200 annually (10 requests/week at $20/hr).', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },

      { type: 'output', content: '┌─ Service Ticket App | NSM IT', className: 'project-title' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   Jan 2025', className: 'project-text' },
      { type: 'output', content: '│ Technologies:', className: 'project-label' },
      { type: 'output', content: '│   Power Platform, SharePoint, HTML, CSS', className: 'project-text' },
      { type: 'output', content: '│ Impact:', className: 'project-label' },
      { type: 'output', content: '│ - Addressed a manual service ticket process (100+ monthly requests).', className: 'project-text' },
      { type: 'output', content: '│ - Built a Power Apps ticketing system with automated email notifications for approvals', className: 'project-text' },
      { type: 'output', content: '│   and assignments, reducing need for manual follow-up.', className: 'project-text' },
      { type: 'output', content: '│ - Decreased manual handling time by ~75% (~30 hours/month) and ~$600 in labor costs.', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' }
    ],
    experience: () => [
      { type: 'output', content: '╔════════════════════════════════════════════════════════╗', className: 'box' },
      { type: 'output', content: '║                 WORK EXPERIENCE                        ║', className: 'box' },
      { type: 'output', content: '╚════════════════════════════════════════════════════════╝', className: 'box' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ Tokio Marine HCC', className: 'project-title' },
      { type: 'output', content: '│ Position Type:', className: 'project-label' },
      { type: 'output', content: '│   IT Applications Intern', className: 'project-text' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   Summer 2026', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },  
      { type: 'output', content: '┌─ UH College of Natural Sciences and Mathematics | NSM IT', className: 'project-title' },
      { type: 'output', content: '│ Position Type:', className: 'project-label' },
      { type: 'output', content: '│   Federal College Work-Study', className: 'project-text' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   Sept 2024 - Present', className: 'project-text' },
      { type: 'output', content: '│ Responsibilities:', className: 'project-label' },
      { type: 'output', content: '│ - Designing and developing full-stack Power Platform solutions,', className: 'project-text' },
      { type: 'output', content: '│   leveraging automation and AI tools to streamline manual administrative', className: 'project-text' },
      { type: 'output', content: '│   workflows across NSM College departments.', className: 'project-text' },
      { type: 'output', content: '│ - Providing technical support and training to students, faculty,', className: 'project-text' },
      { type: 'output', content: '│   and staff across NSM College.', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' }
    ],
    leadership: () => [
      { type: 'output', content: '╔════════════════════════════════════════════════════════╗', className: 'box' },
      { type: 'output', content: '║              LEADERSHIP EXPERIENCE                     ║', className: 'box' },
      { type: 'output', content: '╚════════════════════════════════════════════════════════╝', className: 'box' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ University of Houston Student Recruiter | UH College of NSM Student Leadership', className: 'project-title' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   May 2025 – Current', className: 'project-text' },
      { type: 'output', content: '│ Responsibilities:', className: 'project-label' },
      { type: 'output', content: '│ - Leading campus tours and recruitment events for 500+ prospective', className: 'project-text' },
      { type: 'output', content: '│   students throughout each semester.', className: 'project-text' },
      { type: 'output', content: '│ - Collaborating with staff during outreach and tabling initiative', className: 'project-text' },
      { type: 'output', content: '│   through 3+ high school districts.', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ Scholar Enrichment Program Tutor | UH College of NSM', className: 'project-title' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   May 2025 – Current', className: 'project-text' },
      { type: 'output', content: '│ Responsibilities:', className: 'project-label' },
      { type: 'output', content: '│ - Providing tutoring for data structures, programming, and algorithms', className: 'project-text' },
      { type: 'output', content: '│   to students seeking additional academic support.', className: 'project-text' },
      { type: 'output', content: '│ - Supporting over 50 students per semester in preparing for exams', className: 'project-text' },
      { type: 'output', content: '│   and assignments, contributing to a significant increase in pass rates.', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ Corporate Relations Officer | Code[Coogs]', className: 'project-title' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   May 2025 – Dec 2025', className: 'project-text' },
      { type: 'output', content: '│ Responsibilities:', className: 'project-label' },
      { type: 'output', content: '│ - Automating outreach emails to 200+ recruiters and guest speakers', className: 'project-text' },
      { type: 'output', content: '│   using Power Automate and SharePoint.', className: 'project-text' },
      { type: 'output', content: '│ - Managing company contact lists and communication for club events', className: 'project-text' },
      { type: 'output', content: '│   and professional development workshops.', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' }
    ],
    education: () => [
      { type: 'output', content: '╔════════════════════════════════════════════════════════╗', className: 'box' },
      { type: 'output', content: '║                    EDUCATION                           ║', className: 'box' },
      { type: 'output', content: '╚════════════════════════════════════════════════════════╝', className: 'box' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ University of Houston', className: 'project-title' },
      { type: 'output', content: '│ Location:', className: 'project-label' },
      { type: 'output', content: '│   Houston, TX', className: 'project-text' },
      { type: 'output', content: '│ Degree:', className: 'project-label' },
      { type: 'output', content: '│   Bachelor of Science, Computer Science', className: 'project-text' },
      { type: 'output', content: '│ Graduation Date:', className: 'project-label' },
      { type: 'output', content: '│   May 2027', className: 'project-text' },
      { type: 'output', content: '│ Relevant Coursework:', className: 'project-label' },
      { type: 'output', content: '│   Data Structures, Algorithms, Operating Systems, Databases, Artificial Intelligence', className: 'project-text' },
      { type: 'output', content: '│ GPA:', className: 'project-label' },
      { type: 'output', content: '│   3.68 / 4.0', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ Arthur Miller Career Center', className: 'project-title' },
      { type: 'output', content: '│ Location:', className: 'project-label' },
      { type: 'output', content: '│   Katy, TX', className: 'project-text' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   August 2023 - May 2024', className: 'project-text' },
      { type: 'output', content: '│ Achievement:', className: 'project-label' },
      { type: 'output', content: '│   Earned CompTIA Security+ certification', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' }
    ],
    certifications: () => [
      { type: 'output', content: '╔════════════════════════════════════════════════════════╗', className: 'box' },
      { type: 'output', content: '║                 CERTIFICATIONS                         ║', className: 'box' },
      { type: 'output', content: '╚════════════════════════════════════════════════════════╝', className: 'box' },
      { type: 'output', content: '┌─ CompTIA Security+', className: 'cert-title' },
      { type: 'output', content: '│ Issuer:', className: 'cert-label'},
      { type: 'output', content: '│   CompTIA', className: 'cert-line'},
      { type: 'output', content: '│ Description:', className: 'cert-label' },
      { type: 'output', content: '│   Validates baseline skills for IT security professionals', className: 'cert-text' },
      { type: 'output', content: '│   and demonstrates competency in core security knowledge', className: 'cert-text' },
      { type: 'output', content: '│   and skills.', className: 'cert-text' },
      { type: 'output', content: '│ Link to certification:', className: 'cert-label' },
      { type: 'output', content: '│   https://www.credly.com/badges/80cbc210-78ac-43cd-8803-14a41c12d151', className: 'link' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'cert-line' },
      { type: 'output', content: '', className: '' }
    ],
    skills: () => [
      { type: 'output', content: '╔════════════════════════════════════════════════════════╗', className: 'box' },
      { type: 'output', content: '║                      SKILLS                            ║', className: 'box' },
      { type: 'output', content: '╚════════════════════════════════════════════════════════╝', className: 'box' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ Programming', className: 'project-title' },
      { type: 'output', content: '│   Microsoft Power FX, C++, Java, JavaScript, Python,', className: 'project-text' },
      { type: 'output', content: '│   HTML, CSS, SQL', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ Tools & Platforms', className: 'project-title' },
      { type: 'output', content: '│   Power Apps, Power Automate, Microsoft CoPilot,', className: 'project-text' },
      { type: 'output', content: '│   SharePoint, Power BI, PowerShell, Git, Active Directory', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ Languages', className: 'project-title' },
      { type: 'output', content: '│   English, Arabic', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' }
    ],
    honors: () => [
      { type: 'output', content: '╔════════════════════════════════════════════════════════╗', className: 'box' },
      { type: 'output', content: '║                  HONORS & AWARDS                       ║', className: 'box' },
      { type: 'output', content: '╚════════════════════════════════════════════════════════╝', className: 'box' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ Academic Excellence Scholarship', className: 'project-title' },
      { type: 'output', content: '│ Issuer:', className: 'project-label' },
      { type: 'output', content: '│   University of Houston', className: 'project-text' },
      { type: 'output', content: '│ Years:', className: 'project-label' },
      { type: 'output', content: '│   2024, 2025, 2026', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ Scholar Enrichment Program Scholarship', className: 'project-title' },
      { type: 'output', content: '│ Issuer:', className: 'project-label' },
      { type: 'output', content: '│   UH College of NSM', className: 'project-text' },
      { type: 'output', content: '│ Date:', className: 'project-label' },
      { type: 'output', content: '│   Fall 2025', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ Dean\'s List', className: 'project-title' },
      { type: 'output', content: '│ Issuer:', className: 'project-label' },
      { type: 'output', content: '│   University of Houston', className: 'project-text' },
      { type: 'output', content: '│ Semesters:', className: 'project-label' },
      { type: 'output', content: '│   Fall 2024, Spring 2025', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ President\'s Award for Educational Excellence', className: 'project-title' },
      { type: 'output', content: '│ Year:', className: 'project-label' },
      { type: 'output', content: '│   2024', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' },
      { type: 'output', content: '┌─ AP Scholar with Distinction', className: 'project-title' },
      { type: 'output', content: '│ Years:', className: 'project-label' },
      { type: 'output', content: '│   2023, 2024', className: 'project-text' },
      { type: 'output', content: '└─────────────────────────────────────────────────────────', className: 'project-line' },
      { type: 'output', content: '', className: '' }
    ],
    resume: () => {
      const resumeUrl = 'https://www.dropbox.com/scl/fi/4t7ui1yhd4ek53p0btmgp/IbrahimResume_10-26-2025.pdf?rlkey=8ykt6ptckwrah2fibtgevdvii&st=8x6zfwwx&dl=0';
      // Open resume in new tab immediately
      window.open(resumeUrl, '_blank', 'noopener,noreferrer');
      return [
        { type: 'output', content: 'Link to resume', className: 'link-label', url: resumeUrl },
        { type: 'output', content: '', className: '' }
      ];
    },
    clear: () => []
  };

  const executeCommand = (cmd) => {
    const trimmedCmd = cmd.trim().toLowerCase();

    if (!trimmedCmd) {
      return [];
    }

    // Handle 'ls' command — list navigable sections like directories
    if (trimmedCmd === 'ls') {
      const dirs = ['about', 'projects', 'experience', 'leadership', 'education', 'skills', 'honors', 'certifications', 'resume'];
      return [
        { type: 'output', content: dirs.join('  '), className: 'command-item' },
        { type: 'output', content: '', className: '' }
      ];
    }

    // Handle 'cd <section>' — navigate to a section like a directory
    if (trimmedCmd.startsWith('cd ')) {
      const target = trimmedCmd.slice(3).trim();
      if (!target || target === '~' || target === '/') {
        return [
          { type: 'output', content: 'Type \'help\' to see available commands.', className: 'info' },
          { type: 'output', content: '', className: '' }
        ];
      }
      // Reuse executeCommand logic for the target
      return executeCommand(target);
    }

    if (commands[trimmedCmd]) {
      return commands[trimmedCmd]();
    }

    // Prefix matching for commands (e.g., 'cert' -> 'certifications')
    const matchingCommands = Object.keys(commands).filter((key) => key.startsWith(trimmedCmd));
    if (matchingCommands.length === 1) {
      return commands[matchingCommands[0]]();
    }
    if (matchingCommands.length > 1) {
      return [
        { type: 'output', content: `Ambiguous command: ${cmd}. Did you mean: ${matchingCommands.join(', ')}?`, className: 'error' },
        { type: 'output', content: 'Type \'help\' to see available commands.', className: 'info' },
        { type: 'output', content: '', className: '' }
      ];
    }

    return [
      { type: 'output', content: `Command not found: ${cmd}`, className: 'error' },
      { type: 'output', content: 'Type \'help\' to see available commands.', className: 'info' },
      { type: 'output', content: '', className: '' }
    ];
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Play click sound on every command
    if (clickSoundRef.current) {
      clickSoundRef.current.currentTime = 0;
      clickSoundRef.current.play().catch(() => {});
    }

    const trimmedInput = input.trim().toLowerCase();
    
    // Check if input matches clear command (exact or prefix)
    const matchingCommands = Object.keys(commands).filter((key) => key.startsWith(trimmedInput));
    const isClearMatch = trimmedInput === 'clear' || (matchingCommands.length === 1 && matchingCommands[0] === 'clear');
    
    if (isClearMatch) {
      setHistory([
        { type: 'output', content: 'Type \'help\' to see available commands.', className: 'info' },
        { type: 'output', content: '', className: '' }
      ]);
      setCommandHistory([...commandHistory, input]);
      setHistoryIndex(-1);
      setInput('');
      return;
    }

    // Add command to history
    const newHistory = [...history];
    newHistory.push({ type: 'command', content: input });
    
    // Execute command and add output
    const output = executeCommand(input);
    newHistory.push(...output);

    setHistory(newHistory);
    setCommandHistory([...commandHistory, input]);
    setHistoryIndex(-1);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex < 0 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex >= 0) {
        const newIndex = historyIndex + 1;
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1);
          setInput('');
        } else {
          setHistoryIndex(newIndex);
          setInput(commandHistory[newIndex]);
        }
      }
    }
  };

  return (
    <div className="terminal-container">
      <div className="terminal-body" ref={terminalRef}>
        {history.map((item, index) => (
          <div key={index} className={`terminal-line ${item.className}`}>
            {item.type === 'command' ? (
              <>
                <span className="prompt">ibrahim@portfolio:~$</span>
                <span className="command">{item.content}</span>
              </>
            ) : (
              <div className="output-content">
                {item.className === 'link-label' && item.url ? (
                  <span>
                    <a
                      className="link-text"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.content}
                    </a>
                  </span>
                ) : item.className === 'link' ? (
                  (() => {
                    const urlMatch = item.content.match(/https?:\/\/[^\s]+/);
                    const url = urlMatch ? urlMatch[0] : null;
                    const prefix = url ? item.content.replace(url, '') : item.content;
                    const label = 'Open link';
                    return (
                      <span>
                        <span>{prefix}</span>
                        {url ? (
                          <a
                            className="link-text"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {label}
                          </a>
                        ) : null}
                      </span>
                    );
                  })()
                ) : (
                  <span>{item.content}</span>
                )}
              </div>
            )}
          </div>
        ))}
        <form onSubmit={handleSubmit} className="terminal-input-form">
          <span className="prompt">ibrahim@portfolio:~$</span>
          <input
            ref={inputRef}
            type="text"
            className="terminal-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            spellCheck="false"
          />
        </form>
      </div>
    </div>
  );
};

export default Terminal;

